import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';
import { Form, Formik } from 'formik';
import React from 'react';
import { RentalStepProps } from 'rental/app/steps';
import { RentalPageTitleHelmet } from 'rental/components/PageTitleHelmet';
import { PageContainer } from 'shared/components/PageContainer';
import { PopoverButton } from 'shared/components/PopoverButton';
import { PageHeading } from 'shared/components/typography';
import { EnumOptions, SelectFieldGroup } from 'shared/formik/SelectFieldGroup';
import { TextFieldGroup } from 'shared/formik/TextFieldGroup';
import { getSubmitActionsProps } from 'shared/forms/helpers';
import { cleanPhoneNumber } from 'shared/utils/phone';
import { UseQueryState } from 'urql';
import * as Yup from 'yup';
import { LoadingLabel } from '../../../shared/components/Loading';
import { SubmitActions } from '../../../shared/components/SubmitActions';
import { yupPhoneNumber, yupRequiredMsg } from '../../../shared/yup';
import { MutationErrors } from '../../../shared/components/MutationErrors';
import { ApplicationLivingExpensesPercentage, ApplicationWorkBasis, App_ApplicationFragment, IncomeFragment, IncomeQuery, useIncomeQuery, useUpdateIncomeMutation } from '../../generated.graphql';
import { OccupationTypeahead } from './OccupationTypeAhead';
import { UpdateIncomeInput } from 'shared/generated.graphql';


interface Props extends RentalStepProps {
  application: App_ApplicationFragment
}



export const isEmployed = (data: {workBasis?: string | null} | string | null) => 
  !([
      '', 
      ApplicationWorkBasis.Government, 
      ApplicationWorkBasis.None
    ] as string[]).includes(typeof data === "string" ? data : data?.workBasis || '');


// const phoneNumberRegExp = ' *(\(?[ -]*\d[ -]*\d[ -]*\)?|\+614)[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*$';

interface Values extends Omit<IncomeFragment, 'livingExpensesPercentage' | 'workTimeYears' | 'workTimeMonths'> {
  livingExpensesPercentage: ApplicationLivingExpensesPercentage | ''

  // When using <input type="text"/>, the value provided to the onSubmit handler is always a string due to 
  // the behavior of HTML form inputs. TypeScript types and Yup validation schemas influence compile-time 
  // checks and validation logic but do not alter the runtime behavior of form inputs. 
  // So, specifying these as strings so that they are correctly typed in the onSubmit handler.
  // https://chat.openai.com/share/8072a536-c063-4e5d-a815-f5d737a46213
  workTimeYears?: string | null
  workTimeMonths?: string | null
}


const numberGte0 = Yup.number().typeError('Must be a number').integer().min(0).nullable();


const getLivingExpensesPercentageValidation = (required: boolean, choices: string[]) => {

  // livingExpensesPercentage
  const allowedLivExpValues = [
    ...choices,
    ...required ?  [] : [null]
  ]
  let y = Yup.mixed().oneOf(allowedLivExpValues, 'Select valid option')
  if (required) {
    y = y.required(yupRequiredMsg)
  }
  return y
}

const getValidationSchema = (queryResult: UseQueryState<IncomeQuery, object>) => {
  const workBasisChoices = queryResult.data?.workBasisEnum?.enumValues?.map(v => v.name) || [];
  const livingExpensesChoices = queryResult.data?.livingExpensesEnum?.enumValues?.map(v => v.name) || [];
  const livingExpensesRequired = queryResult.data?.portal?.myApplication?.householdFieldsRequired || false;

  return Yup.object({
    workBasis: Yup.mixed()
      .oneOf(workBasisChoices, 'Required'),
    occupation: Yup.string()
      .when('workBasis', {is: isEmployed, then: Yup.string().required(yupRequiredMsg)}),
    employer: Yup.string()
      .when('workBasis', {is: isEmployed, then: Yup.string().required(yupRequiredMsg)}),
    // phoneWork: Yup.string().matches(/^ *\(?[ -]*\d[ -]*\d[ -]*\)?[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*\d[ -]*$/g, 'Enter a valid Australian phone number.  Must'),
    phoneWork: yupPhoneNumber
      .when('workBasis', {is: isEmployed, then: Yup.string().required(yupRequiredMsg)}),
    workTimeYears: numberGte0
      .when('workBasis', {is: isEmployed, then: numberGte0.required(yupRequiredMsg)}),
    workTimeMonths: numberGte0
      .when('workTimeYears', {is: (y: number) => y !== null && y >= 0 && y < 5, then: numberGte0.required(yupRequiredMsg)}),
    livingExpensesPercentage: getLivingExpensesPercentageValidation(livingExpensesRequired, livingExpensesChoices),
  })
}

export const Income = (props: Props) => {
  const [result,] = useIncomeQuery({variables: {portalSlug: props.application.portal.slug}});
  const income = result.data?.portal?.myApplication?.income
  const [, mutate] = useUpdateIncomeMutation();
  const formRef = React.useRef<any>();
  if (!income) return <LoadingLabel />


  return (
    <PageContainer>
      <RentalPageTitleHelmet>Employment</RentalPageTitleHelmet>
      <PageHeading>My Employment</PageHeading>
      <Formik<Values>
        initialValues={{
          ...income,
          // Since we are using <input type="text"/> for these values, they need to be converted to strings
          workTimeYears: income?.workTimeYears?.toString() || '',
          workTimeMonths: income?.workTimeMonths?.toString() || '',
          livingExpensesPercentage: income?.livingExpensesPercentage || '',
        }}
        validateOnChange={false}
        validationSchema={getValidationSchema(result)}
        // validate={async values => {
        //   // console.log(values);
        //   const input = {
        //     ...omit(values, ['__typename']), 
        //     livingExpensesPercentage: values.livingExpensesPercentage || null as ApplicationLivingExpensesPercentage | null,
        //     portalSlug: props.application.portal.slug, 
        //     workTimeYears: values.workTimeYears || null,
        //     workTimeMonths: values.workTimeMonths || null,
        //     // __typename: undefined
        //     // validateOnly: true,
        //   }
        //   const r = await mutate({input});
        //   if (r.data?.updateIncome?.application) {
        //     props.onSubmitSuccessful()
        //   }
        //   else {
        //     props.onSubmitFailure();
        //   }
        //   const errors = r.data?.updateIncome?.errors?.reduce((p,c) => ({...p, [c.field]: c.messages[0]}), {} as FormikErrors<Values>)
        //   // console.log(r)
        //   return errors
        // }}
        onSubmit={async (data: Values, formikHelpers) => {
          
          const {__typename, ...values} = data;
          const input: UpdateIncomeInput = {
            ...values, 
            // Since we are using <input type="text"/> for these values, they need to be converted to int's before submission to the graph.
            workTimeYears: parseInt(values.workTimeYears ?? '') ?? null,
            workTimeMonths: parseInt(values.workTimeMonths ?? '') ?? null,

            livingExpensesPercentage: (data.livingExpensesPercentage || null) as ApplicationLivingExpensesPercentage | null,
            portalSlug: props.application.portal.slug, 
            phoneWork: cleanPhoneNumber(values.phoneWork),
          }
          
          const r = await mutate({input});

          formikHelpers.setStatus(r.data?.updateIncome?.errors || null)

          if (r.data?.updateIncome?.application) {
            props.onSubmitSuccessful()
          }
          else {
            props.onSubmitFailure();
          }
        }}
      >
        {formik => 
          <Form ref={formRef}>
            {formik.status && <MutationErrors errors={formik.status}/>}
            {/* <p>Occupation value: {formik.values.occupation}, errors: {formik.errors.occupation}, touched: {formik.touched.occupation ?'y':'n'}</p> */}
            <div className="form-row">
              <SelectFieldGroup name="workBasis" className="col-md-6" label="Main source of income" required blank custom>
                <EnumOptions enum={result.data?.workBasisEnum}/>
              </SelectFieldGroup>
              {isEmployed(formik.values) && <>
                {/* <TextFieldGroup name="occupation" className="col-md-6" label="Occupation" disabled={!isEmployed(formik.values) && !!formik.values.workBasis}/> */}
                <OccupationTypeahead 
                  name="occupation" 
                  className="col-md-6" 
                  label="Occupation" 
                  disabled={!isEmployed(formik.values) && !!formik.values.workBasis} 
                  options={Array.from(result.data?.occupations || [])}
                />
                {/* <Typeahead options={[]}/> */}
                </>
              }
            </div>
            {isEmployed(formik.values) && (
              <>
                <div className="form-row">
                  <TextFieldGroup name="employer" className="col-md-6" label="Name of employer" disabled={!isEmployed(formik.values) && !!formik.values.workBasis}/>
                  <div className="col-md-6 d-flex">
                    <TextFieldGroup
                      name="phoneWork"
                      type="tel"
                      pattern="\d*"
                      className="w-100"
                      label="Employer's phone number"
                      disabled={!isEmployed(formik.values) && !!formik.values.workBasis}
                      append={
                        <PopoverButton buttonText={<FontAwesomeIcon icon={faInfoCircle}/>} className="btn btn-link">
                          <p>Don't worry, we won't contact your employer in regards to approving your application.</p>
                        </PopoverButton>}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="col-md-6">
                    <p className={cs("form-control-plaintext", !isEmployed(formik.values) && 'text-muted')}>How long you have worked here?</p>
                  </div>
                  <TextFieldGroup name="workTimeYears" className="col-6 col-sm-4 col-md-3 col-lg-2" label="Years" pattern="[0-9]*" formNoValidate/>
                  <TextFieldGroup name="workTimeMonths" className="col-6 col-sm-4 col-md-3 col-lg-2" label="Months" pattern="[0-9]*" formNoValidate/>
                </div>
              </>
            )}
            {formik.values.workBasis && !props.application.bankStatementsRequired && (
              <>
                <div className="row">
                  <div className="col-md-12">
                    <p className="form-control-plaintext">Amount of income used for living expenses:</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 d-flex">
                    <SelectFieldGroup name="livingExpensesPercentage" label="Income vs expenses ratio" blank className="flex-grow-1" custom>
                      <EnumOptions enum={result.data?.livingExpensesEnum}/>
                    </SelectFieldGroup>
                    <PopoverButton buttonText={<FontAwesomeIcon icon={faInfoCircle}/>} className="btn btn-link mb-3">
                      <p className="font-weight-bold">How do I calculate this?</p>
                      <p>Living expenses are amounts required to meet your normal standard of living. 
                        This would usually include food, rent, mortgage repayments, transport, insurance, 
                        utilities, school fees, childcare costs, any other ongoing debt repayments and any 
                        other amounts suitable to your situation. If you are unsure what to include, 
                        please select ‘I don’t know’.</p>
                      <p className="font-weight-bold">What are living expenses?</p>
                      <p>Living expenses are amounts required to meet your normal standard of living. 
                        This would usually include food, rent, mortgage repayments, transport, insurance, 
                        utilities, school fees, childcare costs, any other ongoing debt repayments and any 
                        other amounts suitable to your situation. If you are unsure what to include, 
                        please select ‘I don’t know’.</p>
                    </PopoverButton>
                  </div>
                </div>
              </>
            )}
            <SubmitActions {...getSubmitActionsProps(formik, props)}/>
          </Form>
        }
      </Formik>
    </PageContainer>
  );
}
